import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TV_SELLER_ROWS, type TvSellerSettings, type TvSellersAdmin } from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  CardBody,
  CollapsibleCard,
  Label,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';

/**
 * "Salg pr. sælger" on /admin/tv-dashboard: the goal metric in CRM, and the sellers marked "Vis" -
 * stored as `DASHBOARD_TV_SELLERS`, see tv-sellers.ts in the API. A seller is on the screen when
 * marked here and CRM has a goal for them this quarter; the goal's figures are CRM's own.
 *
 * Linked by email: the user here to the CRM user with the same `internalemailaddress`. The goal
 * count beside each user is what CRM has this quarter on the metric chosen, saved or not - 1 is
 * right, more is a duplicate. Only users with a goal are listed, and those marked.
 */

const SELLERS_KEY = ['admin', 'tv-dashboard', 'sellers'] as const;

export function TvSellersSettings() {
  const queryClient = useQueryClient();
  const canEdit = useAuth().can('tv_dashboard.manage');
  const [draft, setDraft] = useState<TvSellerSettings | null>(null);
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  /* Without a draft the API counts on the saved metric. */
  const draftMetric = draft?.metric_id ?? null;
  const query = useQuery({
    queryKey: [...SELLERS_KEY, draftMetric],
    queryFn: () => api.get<TvSellersAdmin>('/tv-dashboard/sellers', { metric_id: draftMetric }),
    placeholderData: keepPreviousData,
  });

  const saved = query.data?.settings ?? { metric_id: null, user_ids: [] };
  const current = draft ?? saved;
  const dirty =
    draft !== null &&
    (draft.metric_id !== saved.metric_id || draft.user_ids.join() !== saved.user_ids.join());

  const save = useMutation({
    mutationFn: (next: TvSellerSettings) => api.put<TvSellersAdmin>('/tv-dashboard/sellers', next),
    onMutate: () => setMessage(null),
    onSuccess: (result) => {
      queryClient.setQueryData([...SELLERS_KEY, null], result);
      setDraft(null);
      setMessage({
        tone: 'info',
        text: 'Sælgerne er gemt. Skærmen viser dem ved sin næste opdatering, senest om fem minutter.',
      });
    },
    onError: (cause) =>
      setMessage({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Kunne ikke gemme sælgerne.',
      }),
  });

  const toggle = (id: string): void => {
    const shown = current.user_ids.includes(id);
    setDraft({
      ...current,
      user_ids: shown ? current.user_ids.filter((user) => user !== id) : [...current.user_ids, id],
    });
  };

  const data = query.data;
  const full = current.user_ids.length >= TV_SELLER_ROWS;
  /* A marked user stays listed without a goal, so they can be unmarked. */
  const users = (data?.users ?? []).filter(
    (user) =>
      current.user_ids.includes(user.id) ||
      (user.active && current.metric_id !== null && user.goal_count > 0),
  );

  return (
    <CollapsibleCard
      title="Salg pr. sælger"
      description={
        <>
          Sælgernes salgsmål fra CRM (Performance → Goals) for kvartalet. Budget, vundet og i gang
          er CRM&apos;s egne tal. Højst {TV_SELLER_ROWS} sælgere; de vises i den rækkefølge, de er
          valgt.
        </>
      }
    >
      <CardBody className="space-y-4">
        {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
        {query.isError ? <Alert tone="error">Kunne ikke hente sælgerne.</Alert> : null}
        {data?.error ? <Alert tone="warning">{data.error}</Alert> : null}

        {query.isLoading || !data ? (
          <Skeleton className="h-28" />
        ) : (
          <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-4 border-0 p-0">
            <div className="space-y-2">
              <Label htmlFor="tv-sales-metric">Salgsmålets metrik</Label>
              <Select
                id="tv-sales-metric"
                className="w-72"
                value={current.metric_id ?? ''}
                onChange={(event) =>
                  setDraft({ ...current, metric_id: event.target.value || null })
                }
              >
                <option value="">Vælg metrik</option>
                {data.metrics.map((metric) => (
                  <option key={metric.id} value={metric.id}>
                    {metric.name}
                  </option>
                ))}
                {current.metric_id &&
                !data.metrics.some((metric) => metric.id === current.metric_id) ? (
                  <option value={current.metric_id}>{current.metric_id}</option>
                ) : null}
              </Select>
              <p className="text-xs text-ink-muted">
                Metrikken "OneCode Salg" i CRM. Gemmes på id, så et nyt navn i CRM ikke slår den
                fra.
              </p>
            </div>

            {users.length === 0 ? (
              <p className="text-sm text-ink-muted">
                {current.metric_id
                  ? 'Ingen medarbejdere har et salgsmål på metrikken i kvartalet.'
                  : 'Vælg metrikken for at se, hvem der har et salgsmål.'}
              </p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th className="w-16">Vis</Th>
                    <Th>Medarbejder</Th>
                    <Th>Salgsmål i kvartalet</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const shown = current.user_ids.includes(user.id);
                    const position = current.user_ids.indexOf(user.id) + 1;
                    return (
                      <Tr key={user.id}>
                        <Td>
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-brand"
                            aria-label={`Vis ${user.name}`}
                            checked={shown}
                            disabled={!shown && full}
                            onChange={() => toggle(user.id)}
                          />
                        </Td>
                        <Td>
                          <span className="font-medium">{user.name}</span>
                          {shown ? (
                            <span className="ml-2 text-xs text-ink-muted">nr. {position}</span>
                          ) : null}
                          <span className="block text-xs text-ink-muted">{user.email}</span>
                        </Td>
                        <Td>
                          {!current.metric_id ? (
                            <span className="text-xs text-ink-muted">—</span>
                          ) : user.goal_count === 1 ? (
                            <Badge tone="success">1 salgsmål</Badge>
                          ) : user.goal_count > 1 ? (
                            <Badge tone="warning">{user.goal_count} salgsmål — vises som "—"</Badge>
                          ) : (
                            <Badge>Intet salgsmål — vises ikke</Badge>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {canEdit ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={!dirty}
                  loading={save.isPending}
                  onClick={() => save.mutate(current)}
                >
                  Gem
                </Button>
                <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(null)}>
                  Fortryd
                </Button>
              </div>
            ) : null}
          </fieldset>
        )}
      </CardBody>
    </CollapsibleCard>
  );
}
